import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FileText, Plus, AlertTriangle, Search } from 'lucide-react';
import { api } from '../../api/client';
import { EFakturaSettingsPanel } from './EFakturaSettingsPanel';
import { PurchaseInvoicesPage } from './PurchaseInvoicesPage';
import { VatRecordingPage } from './VatRecordingPage';
import { VatDeductionPage } from './VatDeductionPage';

type Tenant = { id: string; name: string };

type Invoice = {
  id: string;
  invoiceNumber: string;
  invoiceTypeCode: string;
  buyerName: string;
  buyerTaxId: string;
  totalWithVat: string;
  status: string;
  issueDate: string;
};

const tabs = ['Izlazne', 'Ulazne', 'KEV', 'EPP', 'Podešavanja'] as const;
type Tab = (typeof tabs)[number];

const statusColors: Record<string, { bg: string; color: string }> = {
  draft: { bg: 'rgba(156,163,175,0.2)', color: '#9ca3af' },
  submitted: { bg: 'rgba(59,130,246,0.2)', color: '#60a5fa' },
  sent: { bg: 'rgba(234,179,8,0.2)', color: '#facc15' },
  approved: { bg: 'rgba(34,197,94,0.2)', color: '#4ade80' },
  rejected: { bg: 'rgba(239,68,68,0.2)', color: '#fca5a5' },
  cancelled: { bg: 'rgba(156,163,175,0.2)', color: '#9ca3af' },
  storno: { bg: 'rgba(249,115,22,0.2)', color: '#fb923c' },
  submit_failed: { bg: 'rgba(239,68,68,0.2)', color: '#fca5a5' },
};

export function EFakturaPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const initialTab: Tab = tabParam === 'inbound' ? 'Ulazne'
    : tabParam === 'kev' ? 'KEV'
    : tabParam === 'epp' ? 'EPP'
    : 'Izlazne';
  const [activeTab, setActiveTab] = useState<Tab>(initialTab);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get<Tenant[]>('/admin/tenants').then((rows) => {
      setTenants(rows);
      if (rows.length > 0) setSelectedTenantId(rows[0].id);
    }).catch(() => {});
  }, []);

  const fetchInvoices = useCallback(async () => {
    if (!selectedTenantId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await api.get<Invoice[]>(`/admin/efaktura/invoices?tenantId=${selectedTenantId}&direction=outbound`);
      setInvoices(data);
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška pri učitavanju faktura');
    } finally {
      setLoading(false);
    }
  }, [selectedTenantId]);

  useEffect(() => {
    if (activeTab === 'Izlazne' && selectedTenantId) fetchInvoices();
  }, [activeTab, selectedTenantId, fetchInvoices]);

  const formatAmount = (n: string | number) =>
    new Intl.NumberFormat('sr-Latn-RS', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n));

  const formatDate = (d: string) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('sr-Latn-RS');
  };

  return (
    <div>
      <header style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--color-text-strong)' }}>E-Fakture</h1>
          <div style={{ marginTop: 4, fontSize: 13, color: 'var(--color-text-muted)' }}>
            Elektronske fakture - SEF integracija
          </div>
        </div>
        <div style={{ flex: 1 }} />
        {activeTab === 'Izlazne' && (
          <button onClick={() => navigate('/efaktura/invoices/new')} style={btnPrimary}>
            <Plus size={14} /> Nova faktura
          </button>
        )}
      </header>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 20, borderBottom: '1px solid var(--color-border)' , overflowX: 'auto' }}>
        {tabs.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '10px 20px',
              fontSize: 13,
              fontWeight: activeTab === tab ? 600 : 500,
              color: activeTab === tab ? 'var(--color-primary)' : 'var(--color-text-muted)',
              background: 'transparent',
              border: 'none',
              borderBottom: activeTab === tab ? '2px solid var(--color-primary)' : '2px solid transparent',
              cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap',
              marginBottom: -1,
            }}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Izlazne tab */}
      {activeTab === 'Izlazne' && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <label style={{ fontSize: 12, color: 'var(--color-text-muted)', fontWeight: 500 }}>Tenant:</label>
            <select
              value={selectedTenantId}
              onChange={(e) => setSelectedTenantId(e.target.value)}
              style={selectStyle}
            >
              {tenants.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>

          {error && (
            <div style={errorBox}>
              <AlertTriangle size={14} /> {error}
            </div>
          )}

          <div style={{ background: 'var(--color-bg-surface)', borderRadius: 10, overflow: 'hidden', border: '1px solid var(--color-border)' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--color-bg)' }}>
                    <th style={th}>Broj</th>
                    <th style={th}>Tip</th>
                    <th style={th}>Kupac</th>
                    <th style={th}>PIB</th>
                    <th style={{ ...th, textAlign: 'right' }}>Iznos</th>
                    <th style={th}>Status</th>
                    <th style={th}>Datum</th>
                    <th style={{ ...th, width: 80 }}>Akcije</th>
                  </tr>
                </thead>
                <tbody>
                  {!loading && invoices.length === 0 && (
                    <tr>
                      <td colSpan={8} style={{ ...td, textAlign: 'center', padding: 40, color: 'var(--color-text-muted)' }}>
                        <FileText size={32} style={{ margin: '0 auto 8px', opacity: 0.3 }} />
                        <div>Nema faktura</div>
                      </td>
                    </tr>
                  )}
                  {invoices.map((inv) => {
                    const sc = statusColors[inv.status] || statusColors.draft;
                    return (
                      <tr key={inv.id} onClick={() => navigate(`/efaktura/invoices/${inv.id}`)} style={{ cursor: 'pointer' }}>
                        <td style={{ ...td, color: 'var(--color-text-strong)', fontWeight: 600 }}>{inv.invoiceNumber || '-'}</td>
                        <td style={{ ...td, color: 'var(--color-text-muted)' }}>{inv.invoiceTypeCode || '-'}</td>
                        <td style={{ ...td, color: 'var(--color-text-strong)' }}>{inv.buyerName || '-'}</td>
                        <td style={{ ...td, color: 'var(--color-text-muted)', fontVariantNumeric: 'tabular-nums' }}>{inv.buyerTaxId || '-'}</td>
                        <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--color-text-strong)' }}>{formatAmount(inv.totalWithVat)}</td>
                        <td style={td}>
                          <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 8, background: sc.bg, color: sc.color, fontWeight: 700, textTransform: 'uppercase' }}>
                            {inv.status}
                          </span>
                        </td>
                        <td style={{ ...td, color: 'var(--color-text-muted)', fontVariantNumeric: 'tabular-nums' }}>{formatDate(inv.issueDate)}</td>
                        <td style={{ ...td }} onClick={(e) => e.stopPropagation()}>
                          <button onClick={() => navigate(`/efaktura/invoices/${inv.id}`)} style={iconBtn} title="Otvori"><Search size={13} /></button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Ulazne tab */}
      {activeTab === 'Ulazne' && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <label style={{ fontSize: 12, color: 'var(--color-text-muted)', fontWeight: 500 }}>Tenant:</label>
            <select
              value={selectedTenantId}
              onChange={(e) => setSelectedTenantId(e.target.value)}
              style={selectStyle}
            >
              {tenants.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
          <PurchaseInvoicesPage tenantId={selectedTenantId} />
        </>
      )}

      {/* KEV tab */}
      {activeTab === 'KEV' && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <label style={{ fontSize: 12, color: 'var(--color-text-muted)', fontWeight: 500 }}>Tenant:</label>
            <select
              value={selectedTenantId}
              onChange={(e) => setSelectedTenantId(e.target.value)}
              style={selectStyle}
            >
              {tenants.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
          <VatRecordingPage tenantId={selectedTenantId} />
        </>
      )}

      {/* EPP tab */}
      {activeTab === 'EPP' && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <label style={{ fontSize: 12, color: 'var(--color-text-muted)', fontWeight: 500 }}>Tenant:</label>
            <select
              value={selectedTenantId}
              onChange={(e) => setSelectedTenantId(e.target.value)}
              style={selectStyle}
            >
              {tenants.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
          <VatDeductionPage tenantId={selectedTenantId} />
        </>
      )}

      {/* Podesavanja tab */}
      {activeTab === 'Podešavanja' && <EFakturaSettingsPanel />}
    </div>
  );
}

/* ─── Style constants ─── */
const th: React.CSSProperties = { padding: '12px 14px', fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left', borderBottom: '1px solid var(--color-border)' };
const td: React.CSSProperties = { padding: '12px 14px', fontSize: 13, color: 'var(--color-text)', borderBottom: '1px solid var(--color-border)' };
const btnPrimary: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 6, border: 'none', background: 'var(--color-primary)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const iconBtn: React.CSSProperties = { background: 'transparent', border: 'none', color: 'var(--color-text-muted)', padding: 6, marginLeft: 4, borderRadius: 4, cursor: 'pointer' };
const errorBox: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 6, background: 'rgba(239,68,68,0.15)', color: '#fca5a5', fontSize: 13, marginBottom: 16 };
const selectStyle: React.CSSProperties = { padding: '8px 12px', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 13, background: 'var(--color-bg-surface)', color: 'var(--color-text)', minWidth: 200 };
